import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Phone, MapPin, Car, Tag, Calendar, MessageSquare, Plus, ArrowLeft, Edit, Star } from 'lucide-react';
import { useApp } from '../context/AppContext';
import Badge from '../components/Badge';
import Breadcrumb from '../components/Breadcrumb';
import Modal from '../components/Modal';
import { Select } from '../components/FormComponents';
import { formatDate, formatDateTime, getLeadScore } from '../utils/helpers';

const STATUSES = ['Hot', 'Warm', 'Cold'];

export default function CustomerDetail() {
  const { id } = useParams();
  const { state, dispatch, showNotification } = useApp();
  const [noteText, setNoteText] = useState('');
  const [showEditStatus, setShowEditStatus] = useState(false);
  const [newStatus, setNewStatus] = useState('');

  const prospect = state.prospects.find(p => p.id === id);
  const notes = state.notes[id] || [];
  const relatedSPK = state.spk.filter(s => s.namaPembeli === prospect?.nama);
  const relatedDelivery = state.delivery.filter(d => d.namaPembeli === prospect?.nama);

  if (!prospect) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-400">Prospek tidak ditemukan</p>
        <Link to="/prospecting" className="btn-primary mt-4 inline-flex">Kembali ke Prospecting</Link>
      </div>
    );
  }

  const score = getLeadScore(prospect);

  const addNote = () => {
    if (!noteText.trim()) return;
    dispatch({ type: 'ADD_NOTE', payload: { customerId: id, text: noteText } });
    setNoteText('');
    showNotification('Catatan berhasil ditambahkan');
  };

  const updateStatus = () => {
    dispatch({ type: 'UPDATE_PROSPECT', payload: { ...prospect, status: newStatus } });
    showNotification('Status berhasil diperbarui');
    setShowEditStatus(false);
  };

  const scoreColor = score >= 70 ? 'text-green-600' : score >= 40 ? 'text-orange-500' : 'text-red-500';
  const scoreBarColor = score >= 70 ? 'bg-green-500' : score >= 40 ? 'bg-orange-500' : 'bg-red-500';

  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: 'Prospecting', path: '/prospecting' }, { label: prospect.nama }]} />

      <div className="flex items-center gap-3">
        <Link to="/prospecting" className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"><ArrowLeft size={18} /></Link>
        <h1 className="text-2xl font-bold text-gray-800">{prospect.nama}</h1>
        <Badge status={prospect.status} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <div className="lg:col-span-1 space-y-4">
          <div className="card text-center">
            <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <span className="text-red-600 text-2xl font-bold">{prospect.nama.charAt(0)}</span>
            </div>
            <h2 className="text-lg font-bold text-gray-800">{prospect.nama}</h2>
            <p className="text-sm text-gray-500 mt-1">{prospect.sumberLead}</p>

            {/* Lead Score */}
            <div className="mt-4 p-3 bg-gray-50 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500">Lead Score</span>
                <span className={`text-lg font-bold ${scoreColor}`}>{score}</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full">
                <div className={`h-2 rounded-full transition-all duration-700 ${scoreBarColor}`} style={{ width: `${score}%` }} />
              </div>
            </div>

            <button onClick={() => { setNewStatus(prospect.status); setShowEditStatus(true); }} className="btn-secondary w-full mt-3 justify-center">
              <Edit size={14} /> Update Status
            </button>
          </div>

          <div className="card space-y-3">
            <h3 className="text-sm font-semibold text-gray-700">Informasi Kontak</h3>
            <div className="flex items-center gap-3 text-sm">
              <Phone size={15} className="text-red-500 flex-shrink-0" />
              <a href={`tel:${prospect.noHP}`} className="text-blue-600 hover:underline">{prospect.noHP}</a>
            </div>
            <div className="flex items-start gap-3 text-sm">
              <MapPin size={15} className="text-red-500 flex-shrink-0 mt-0.5" />
              <span className="text-gray-600">{prospect.alamat || '-'}</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Car size={15} className="text-red-500 flex-shrink-0" />
              <span className="text-gray-600">{prospect.mobilDiminati || '-'}</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Tag size={15} className="text-red-500 flex-shrink-0" />
              <Badge status={prospect.sumberLead} />
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Calendar size={15} className="text-red-500 flex-shrink-0" />
              <span className="text-gray-600">{formatDate(prospect.createdAt)}</span>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* SPK */}
          {relatedSPK.length > 0 && (
            <div className="card">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">SPK Terkait</h3>
              <div className="space-y-2">
                {relatedSPK.map(s => (
                  <div key={s.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-700">{s.spkNumber}</p>
                      <p className="text-xs text-gray-400">{s.tipeMobil} • {formatDate(s.createdAt)}</p>
                    </div>
                    <Badge status={s.status} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Delivery */}
          {relatedDelivery.length > 0 && (
            <div className="card">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Delivery Terkait</h3>
              <div className="space-y-2">
                {relatedDelivery.map(d => (
                  <div key={d.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-700">{d.tipeMobil}</p>
                      <p className="text-xs text-gray-400">Tgl Kirim: {formatDate(d.tanggalKirim)}</p>
                    </div>
                    <Badge status={d.statusPengiriman} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="card">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <MessageSquare size={15} className="text-red-500" /> Catatan ({notes.length})
            </h3>
            <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
              {notes.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">Belum ada catatan</p>
              ) : (
                notes.map(note => (
                  <div key={note.id} className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-gray-700">{note.text}</p>
                    <p className="text-xs text-gray-400 mt-1">{formatDateTime(note.createdAt)}</p>
                  </div>
                ))
              )}
            </div>
            <div className="flex gap-2">
              <input
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addNote()}
                placeholder="Tambah catatan baru..."
                className="form-input flex-1"
              />
              <button onClick={addNote} className="btn-primary px-3"><Plus size={16} /></button>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Status Modal */}
      <Modal
        isOpen={showEditStatus}
        onClose={() => setShowEditStatus(false)}
        title="Update Status Lead"
        size="sm"
        footer={<>
          <button onClick={() => setShowEditStatus(false)} className="btn-secondary">Batal</button>
          <button onClick={updateStatus} className="btn-primary">Simpan</button>
        </>}
      >
        <Select
          label="Status Baru"
          value={newStatus}
          onChange={e => setNewStatus(e.target.value)}
          options={STATUSES.map(s => ({ value: s, label: s }))}
        />
      </Modal>
    </div>
  );
}
